/**
 * StepIcon.tsx - Icon component for step types
 *
 * Renders the appropriate icon for each visual step category.
 * Used in both NodePalette and StepPaletteModal.
 *
 * Related:
 *   - components/paletteStepTypes.ts - Visual category definitions
 */

import type { VisualCategory } from './paletteStepTypes';

interface StepIconProps {
	category: VisualCategory | string;
	size?: number;
}

export function StepIcon({ category, size = 16 }: StepIconProps) {
	const iconProps = {
		width: size,
		height: size,
		viewBox: '0 0 16 16',
		fill: 'none',
		stroke: 'currentColor',
		strokeWidth: 1.5,
		strokeLinecap: 'round' as const,
		strokeLinejoin: 'round' as const,
	};

	switch (category) {
		case 'email':
			return (
				<svg {...iconProps}>
					<rect x='2' y='4' width='12' height='9' rx='1' />
					<path d='M2 5l6 4 6-4' />
				</svg>
			);
		case 'aiEmail':
			return (
				<svg {...iconProps}>
					<rect x='2' y='4' width='12' height='9' rx='1' />
					<path d='M2 5l6 4 6-4' />
					<path d='M12.5 1v3M11 2.5h3' />
				</svg>
			);
		case 'texting':
			return (
				<svg {...iconProps}>
					<rect x='4' y='1' width='8' height='14' rx='1' />
					<line x1='7' y1='12' x2='9' y2='12' />
				</svg>
			);
		case 'newsletter':
			return (
				<svg {...iconProps}>
					<rect x='2' y='2' width='12' height='12' rx='1' />
					<line x1='5' y1='5' x2='11' y2='5' />
					<line x1='5' y1='8' x2='11' y2='8' />
					<line x1='5' y1='11' x2='8' y2='11' />
				</svg>
			);
		case 'card':
			return (
				<svg {...iconProps}>
					<rect x='1' y='3' width='14' height='10' rx='1' />
					<path d='M4 7h8M4 9h4' />
				</svg>
			);
		case 'task':
			return (
				<svg {...iconProps}>
					<rect x='2' y='2' width='12' height='12' rx='1' />
					<path d='M5 8l2 2 4-4' />
				</svg>
			);
		case 'opportunity':
			return (
				<svg {...iconProps}>
					<circle cx='8' cy='8' r='6' />
					<path d='M8 5v3l2 2' />
				</svg>
			);
		case 'tag':
			return (
				<svg {...iconProps}>
					<path d='M1 8V1h7l7 7-7 7-7-7z' />
					<circle cx='4.5' cy='4.5' r='1' fill='currentColor' stroke='none' />
				</svg>
			);
		case 'tagRemove':
			return (
				<svg {...iconProps}>
					<path d='M1 8V1h7l7 7-7 7-7-7z' />
					<path d='M6 6l4 4M10 6l-4 4' />
				</svg>
			);
		case 'wait':
			return (
				<svg {...iconProps}>
					<circle cx='8' cy='8' r='6' />
					<path d='M8 4v4l3 2' />
				</svg>
			);
		case 'doNothing':
			return (
				<svg {...iconProps}>
					<circle cx='8' cy='8' r='6' />
					<path d='M5 11L11 5' />
				</svg>
			);
		case 'conditional':
			return (
				<svg {...iconProps}>
					<path d='M8 2v4M8 10v4M4 6h8l-4 4-4-4z' fill='currentColor' strokeWidth={0} />
				</svg>
			);
		case 'triggerAutomation':
			return (
				<svg {...iconProps}>
					<path d='M3 8h7' />
					<path d='M8 5l3 3-3 3' />
					<rect x='11' y='4' width='3' height='8' rx='1' />
				</svg>
			);
		default:
			return (
				<svg {...iconProps}>
					<circle cx='8' cy='8' r='6' />
				</svg>
			);
	}
}
